'use client';

import {
  passwordRules,
  passwordStrength,
} from '@eliadmin/core/src/components/nt/account/password/password-rules';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { splitOptions } from '@eliadmin/core/src/utils/nt/text';

/**
 * Colours of the three levels. Figma draws the strong one (green); weak and medium use the danger
 * red and the CMYK yellow of the design system.
 */
const LEVELS = [
  { bar: 'bg-[#D92D20]', text: 'text-[#B42318]' },
  { bar: 'bg-[#FFD200]', text: 'text-[#0D110D]' },
  { bar: 'bg-[#127A26]', text: 'text-[#0E6A20]' },
] as const;

/**
 * Figma „Strength“ (207:11374): one 4 px bar per rule (radius 2, 6 px apart; met ones in the level
 * colour, the rest #E2E5DF), 8 px below „Sila hesla:“ (Body/M #363D36) and the level (Body/M Bold).
 */
export function PasswordStrength({ password }: { password: string }) {
  const texts = useAccountTexts();
  const strength = passwordStrength(texts.passwordRules, password);
  const bars = splitOptions(texts.passwordRules).length;
  const level = strength ? LEVELS[strength.level] : null;
  const levelLabel = strength ? splitOptions(texts.passwordLevels)[strength.level] : null;
  if (!bars) return null;

  return (
    <div className="flex flex-col gap-2">
      <div aria-hidden="true" className="flex gap-1.5">
        {Array.from({ length: bars }, (_, index) => (
          <span
            key={index}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              level && index < (strength?.met ?? 0) ? level.bar : 'bg-[#E2E5DF]',
            )}
          />
        ))}
      </div>
      {texts.passwordStrength ? (
        <p className="flex gap-1.5">
          <span className="nt-body-m text-[#363D36]">{texts.passwordStrength}</span>
          {levelLabel ? (
            <span className={cn('nt-body-m-bold', level?.text)}>{levelLabel}</span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Figma „Requirements“ (207:11374): the admin rules, 6 px apart – a met rule with the green
 * check-circle in #0D110D, an open one with the grey circle in #5D655D. After a submit that failed
 * on them (`showErrors`) the open required rules turn red with the warning icon.
 */
export function PasswordRules({
  id,
  password,
  showErrors,
}: {
  id: string;
  password: string;
  showErrors: boolean;
}) {
  const texts = useAccountTexts();
  const rules = passwordRules(texts.passwordRules, password);
  if (!rules.length) return null;

  return (
    <ul id={id} role="list" className="flex flex-col gap-1.5">
      {rules.map(rule => {
        const failed = showErrors && rule.required && !rule.met;
        return (
          <li key={rule.label} className="flex items-center gap-2">
            <Icon
              name={rule.met ? 'check-circle' : failed ? 'warning-circle' : 'circle'}
              size={16}
              className={rule.met ? 'text-[#127A26]' : failed ? 'text-[#D92D20]' : 'text-[#8F978E]'}
            />
            <span
              className={cn(
                'nt-body-m',
                rule.met ? 'text-[#0D110D]' : failed ? 'text-[#B42318]' : 'text-[#5D655D]',
              )}
            >
              {rule.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
